// Reported-issue detail drawer — deep-linkable via
// ?tab=legal-review&report={id}. Shows the status-chip ladder, the report
// body, and the per-status triage actions (gated: triage =
// Compliance.ManageExceptions; escalate = Defaults.Manage and HIDDEN — not
// disabled — for non-holders, since escalation IS declaring a default).

import { Link } from "react-router-dom";
import { Badge, Button } from "@/design-system/components/ui";
import type {
  ComplianceIntakeReportDto,
  ComplianceIntakeReportStatus,
} from "@/api/compliance/intakeReportTypes";
import { useDateFormatter } from "@/utils/datetime";
import { ResourceDrawer } from "../common/ResourceDrawer";

export const REPORT_STATUS_BADGE: Record<
  ComplianceIntakeReportStatus,
  React.ComponentProps<typeof Badge>["status"]
> = {
  New: "draft",
  Acknowledged: "info",
  Investigating: "review",
  Resolved: "finalized",
  Escalated: "archived",
};

const LADDER: ComplianceIntakeReportStatus[] = [
  "New",
  "Acknowledged",
  "Investigating",
];

export function severityTone(severity: string | null): string {
  switch (severity) {
    case "Critical":
    case "Major":
      return "text-status-error";
    case "Minor":
      return "text-status-warning";
    default:
      return "text-gray-mid";
  }
}

export type ReportTriageAction =
  | "acknowledge"
  | "investigate"
  | "resolve"
  | "escalate";

interface ReportDetailDrawerProps {
  report: ComplianceIntakeReportDto;
  canTriage: boolean;
  canEscalate: boolean;
  onAction: (action: ReportTriageAction) => void;
  onClose: () => void;
}

export function ReportDetailDrawer({
  report,
  canTriage,
  canEscalate,
  onAction,
  onClose,
}: ReportDetailDrawerProps) {
  const { date } = useDateFormatter();
  const isOpenStatus =
    report.status === "New" ||
    report.status === "Acknowledged" ||
    report.status === "Investigating";
  const severity = report.triageSeverity ?? report.suggestedSeverity;

  return (
    <ResourceDrawer
      open
      onClose={onClose}
      title="Reported issue"
      subtitle={report.category}
      testId="report-detail-drawer"
      footer={
        canTriage && isOpenStatus ? (
          <>
            {report.status === "New" && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onAction("acknowledge")}
                data-testid="report-action-acknowledge"
              >
                Acknowledge
              </Button>
            )}
            {report.status !== "Investigating" && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onAction("investigate")}
                data-testid="report-action-investigate"
              >
                Start investigation
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onAction("resolve")}
              data-testid="report-action-resolve"
            >
              Resolve…
            </Button>
            {canEscalate && (
              <Button
                size="sm"
                onClick={() => onAction("escalate")}
                data-testid="report-action-escalate"
              >
                Escalate to default…
              </Button>
            )}
          </>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-space-3">
        {/* Status-chip ladder. */}
        <div
          className="flex items-center gap-space-2 flex-wrap"
          data-testid="report-status-ladder"
        >
          {LADDER.map((s) => (
            <Badge
              key={s}
              status={s === report.status ? REPORT_STATUS_BADGE[s] : "archived"}
            >
              {s}
            </Badge>
          ))}
          <Badge
            status={
              report.status === "Resolved" || report.status === "Escalated"
                ? REPORT_STATUS_BADGE[report.status]
                : "archived"
            }
          >
            {report.status === "Escalated" ? "Escalated" : "Resolved"}
          </Badge>
        </div>

        <dl className="grid grid-cols-2 gap-x-space-3 gap-y-space-2 text-small m-0">
          <dt className="text-caption text-gray-mid uppercase tracking-label">
            Severity
          </dt>
          <dd className={`m-0 ${severityTone(severity)}`}>
            {severity ?? "Unrated"}
            {!report.triageSeverity && report.suggestedSeverity && (
              <span className="text-caption text-gray-mid"> (suggested)</span>
            )}
          </dd>
          <dt className="text-caption text-gray-mid uppercase tracking-label">
            Reported by
          </dt>
          <dd className="m-0 flex items-center gap-space-2 flex-wrap">
            <span>
              {report.reportedByUserName ?? "Unknown user"} ·{" "}
              {date(report.reportedAt)}
            </span>
            {report.source === "Borrower" && (
              <Badge status="info">Borrower</Badge>
            )}
          </dd>
          {report.acknowledgedAt && (
            <>
              <dt className="text-caption text-gray-mid uppercase tracking-label">
                Acknowledged
              </dt>
              <dd className="m-0">{date(report.acknowledgedAt)}</dd>
            </>
          )}
        </dl>

        <div>
          <p className="text-caption text-gray-mid uppercase tracking-label m-0">
            Description
          </p>
          <p className="text-small mt-space-1 m-0 whitespace-pre-wrap">
            {report.description}
          </p>
        </div>

        {report.status === "Resolved" && report.resolutionReason && (
          <div data-testid="report-resolution">
            <p className="text-caption text-gray-mid uppercase tracking-label m-0">
              Resolution
            </p>
            <p className="text-small mt-space-1 m-0">
              {report.resolutionReason}
              {report.resolvedAt && (
                <span className="text-caption text-gray-mid">
                  {" "}
                  · {date(report.resolvedAt)}
                </span>
              )}
            </p>
          </div>
        )}

        {report.status === "Escalated" && report.escalatedDefaultCaseId && (
          <p className="text-small m-0" data-testid="report-escalated-link">
            Escalated to{" "}
            <Link
              to={`/portfolio-admin/loans/${report.loanId}?tab=defaults&case=${report.escalatedDefaultCaseId}`}
              className="text-gold-shadow hover:underline"
            >
              the default case
            </Link>
            .
          </p>
        )}

        {report.documentId && (
          <p className="text-small m-0">
            <Link
              to={`/portfolio-admin/loans/${report.loanId}?tab=documents`}
              className="text-gold-shadow hover:underline"
              data-testid="report-document-link"
            >
              Supporting document (Documents tab)
            </Link>
          </p>
        )}
      </div>
    </ResourceDrawer>
  );
}
